/**
 * OnboardingChecklist — one outcome-first activation card.
 *
 * Connections and configuration are acquired inside the delegated work. This
 * surface must never turn onboarding back into an integrations checklist.
 */

import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowRight, Sparkles, X } from 'lucide-react'
import { Button } from '@/components/UI/Button/Button'
import type { SetupStep } from '@/hooks/useSetupStatus'

export interface OnboardingChecklistProps {
  steps: SetupStep[]
  /** Unique key for per-session dismiss */
  nudgeKey?: string
}

export const OnboardingChecklist: React.FC<OnboardingChecklistProps> = ({
  steps,
  nudgeKey = 'onboarding_checklist',
}) => {
  const navigate = useNavigate()
  const dismissKey = `nudge_dismissed_${nudgeKey}`
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(dismissKey) === 'true'
  )

  const nextStep = steps.find(s => !s.completed)

  if (dismissed) return null
  if (!nextStep) return null

  const handleDismiss = () => {
    setDismissed(true)
    sessionStorage.setItem(dismissKey, 'true')
  }

  return (
    <article
      className="relative overflow-hidden rounded-[var(--radius-xl)] border border-[var(--border-accent)] bg-[var(--bg-card)] shadow-[var(--shadow-md)]"
    >
      {/* Subtle accent strip on the left */}
      <div
        aria-hidden
        className="absolute left-0 top-0 bottom-0 w-1"
        style={{ background: 'var(--gradient-brand-button)' }}
      />

      <div className="p-5 sm:p-6">
        {/* Header row */}
        <div className="flex items-start gap-3">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: 'color-mix(in srgb, var(--accent) 15%, transparent)' }}
          >
            <Sparkles className="h-4 w-4" style={{ color: 'var(--accent)' }} />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-lg font-semibold text-[var(--text-primary)]">
                {nextStep.label}
              </h2>
              <button
                type="button"
                onClick={handleDismiss}
                className="p-1 rounded transition-colors text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                aria-label="Dismiss"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="mt-1 max-w-2xl text-sm leading-6 text-[var(--text-muted)]">
              {nextStep.description}
            </p>
          </div>
        </div>

        <Button
          variant="primary"
          size="sm"
          iconRight={<ArrowRight className="h-3.5 w-3.5" />}
          onClick={() => navigate(nextStep.path)}
          className="mt-5"
        >
          Ask Ricald
        </Button>
      </div>
    </article>
  )
}

export default OnboardingChecklist
